// Ship loadout (spec-loadout §1–2): the chosen slot-0 weapon plus the
// DnD-like trait point-buy (5 points total, 0–4 per trait), applied at every
// run (re)start. Trait constants live here, not in config.ts. Trait effects
// are run-constant and deterministic: they derive only from
// `player.loadoutTraits`, recorded once by applyLoadout.
//
// Per-point effects:
//   SWIFT   +4% move speed, +4% dash recharge (haste), −4% max HP
//   BULWARK +12 max HP, +5% shield regen, −3% move speed
//   STRIKER +5% damage, +3% fire rate, −6% max HP
//
// Consumption points mirror the boon flow: move speed + dash haste in
// movement.ts, damage in combat.ts playerCombatStats, fire rate in
// weapon-system.ts, max HP + shield regen applied here at run reset.

import { cfg } from '../config.js';
import type { LoadoutTraits, Player, WeaponDef, WeaponState } from '../types.js';
import type { WorldState } from './state.js';
import { RngStream } from './rng.js';
import weaponsData from '../content/weapons.json';

// The trait record shape lives with the Player schema in types.ts; re-export
// so loadout consumers have a single import site.
export type { LoadoutTraits } from '../types.js';

/** Weapons offered on the loadout screen (slot 0). All exist in weapons.json;
 * the unchosen two stay in the boon pool, the manual turret stays in slot 1. */
export const LOADOUT_WEAPON_IDS = [
  'weapon.spear-jelly-lance',
  'weapon.shard-scattergun',
  'weapon.seeker-swarm'
] as const;

export type LoadoutWeaponId = (typeof LOADOUT_WEAPON_IDS)[number];

/** Auto-weapon slot capacity bounds (the manual turret in slot 1 never counts). */
export const MAX_WEAPON_SLOTS = 4;
export const MIN_WEAPON_SLOTS = 1;
/** STRIKER points at or above this add +1 auto weapon slot (capped). */
export const STRIKER_SLOT_BONUS_THRESHOLD = 3;

// Dedicated stream salt for the hull hardpoint roll: keeps the slot count
// independent of the shared world streams and of the quirk/captain salts, so
// the loadout preview and the run agree regardless of other rolls consumed.
const WEAPON_SLOTS_STREAM_SALT = 0xB5297A4D;

/**
 * Hull hardpoint roll: how many AUTO weapon slots the ship supports
 * (MIN–MAX_WEAPON_SLOTS), pure in (shipSeed, striker). Weighted
 * 1:15% / 2:40% / 3:35% / 4:10% from a private mulberry32 stream off the ship
 * seed; STRIKER ≥ STRIKER_SLOT_BONUS_THRESHOLD adds +1 (capped). Used by
 * applyLoadout (run start) and the loadout screen (preview) so the number the
 * player sees is the number they get.
 */
export function rollWeaponSlots(shipSeed: number, striker: number): number {
  const stream = new RngStream((shipSeed ^ WEAPON_SLOTS_STREAM_SALT) >>> 0);
  const roll = stream.next();
  let slots = roll < 0.15 ? 1 : roll < 0.55 ? 2 : roll < 0.9 ? 3 : 4;
  if (striker >= STRIKER_SLOT_BONUS_THRESHOLD) slots += 1;
  return Math.max(MIN_WEAPON_SLOTS, Math.min(MAX_WEAPON_SLOTS, slots));
}

/** A full loadout selection; matches the `la.loadout.v1` storage shape. */
export interface Loadout extends LoadoutTraits {
  weapon: LoadoutWeaponId;
}

export const DEFAULT_LOADOUT: Loadout = {
  weapon: 'weapon.spear-jelly-lance',
  swift: 0,
  bulwark: 0,
  striker: 0
};

/** Trait point-buy rules (spec-loadout §1): 5 points total, 0–4 per trait. */
export const TRAIT_POINTS_TOTAL = 5;
export const TRAIT_POINTS_MAX_PER_TRAIT = 4;

/** Aggregated stat modifiers from the player's recorded trait points. */
export interface TraitMods {
  /** Additive move-speed multiplier (SWIFT +, BULWARK −). */
  moveSpeedMult: number;
  /** Extra dash haste; dash cooldown ticks divide by (1 + haste). */
  dashHasteBonus: number;
  /** Additive "increased damage" bucket contribution (STRIKER). */
  damageMult: number;
  /** Fire-rate multiplier contribution: rate × (1 + fireRateMult). */
  fireRateMult: number;
  /** Absolute max-HP delta; percent parts are taken on the config base max HP. */
  maxHpDelta: number;
  /** Shield-regen bonus, folded in as boon_shield_regen_mult × (1 + bonus). */
  shieldRegenMultBonus: number;
  /** Additive pickup magnet-radius multiplier (captain effect: smooth). */
  magnetRadiusMult: number;
}

const WEAPON_DEFS = (weaponsData as { version: string; entries: WeaponDef[] }).entries;

export function isLoadoutWeaponId(id: unknown): id is LoadoutWeaponId {
  return typeof id === 'string' && (LOADOUT_WEAPON_IDS as readonly string[]).includes(id);
}

/**
 * Aggregate the player's recorded trait points into stat modifiers.
 * Deterministic and run-constant: reads only `player.loadoutTraits` (and the
 * boot-constant config base max HP). Missing traits read as 0/0/0.
 */
export function getTraitMods(player: Player): TraitMods {
  const traits = player.loadoutTraits;
  const swift = traits?.swift ?? 0;
  const bulwark = traits?.bulwark ?? 0;
  const striker = traits?.striker ?? 0;
  // Percent max-HP costs are taken on the config base (boot-constant), so a
  // mid-run max-HP boon never shifts the trait delta.
  const baseMaxHp = cfg.getNumber('player.max_hp');
  // The captain's perk/drawback pair rides the same channel (captain/effects.ts).
  // Their max-HP and shield-regen parts are applied at restart instead.
  const cfx = player.captainEffects;
  return {
    moveSpeedMult: swift * 0.04 + bulwark * -0.03 + (cfx?.moveSpeedMult ?? 0),
    dashHasteBonus: swift * 0.04 + (cfx?.dashHasteBonus ?? 0),
    damageMult: striker * 0.05 + (cfx?.damageMult ?? 0),
    fireRateMult: striker * 0.03 + (cfx?.fireRateMult ?? 0),
    maxHpDelta: bulwark * 12 + (swift * -0.04 + striker * -0.06) * baseMaxHp,
    shieldRegenMultBonus: bulwark * 0.05,
    magnetRadiusMult: cfx?.magnetRadiusMult ?? 0
  };
}

function clampTraitPoints(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(TRAIT_POINTS_MAX_PER_TRAIT, Math.round(value)));
}

/**
 * Coerce arbitrary stored/entered data into a valid loadout: an unknown
 * weapon falls back to the default, each trait clamps to 0–4, and an
 * over-budget spread is trimmed (striker first, then bulwark) until it fits
 * the 5-point total. Deterministic.
 */
export function normalizeLoadout(input: unknown): Loadout {
  const partial = (
    typeof input === 'object' && input !== null ? input : {}
  ) as Partial<Record<keyof Loadout, unknown>>;
  const out: Loadout = {
    weapon: isLoadoutWeaponId(partial.weapon) ? partial.weapon : DEFAULT_LOADOUT.weapon,
    swift: clampTraitPoints(partial.swift),
    bulwark: clampTraitPoints(partial.bulwark),
    striker: clampTraitPoints(partial.striker)
  };
  let total = out.swift + out.bulwark + out.striker;
  for (const key of ['striker', 'bulwark', 'swift'] as const) {
    while (total > TRAIT_POINTS_TOTAL && out[key] > 0) {
      out[key]--;
      total--;
    }
  }
  return out;
}

// What applyLoadout last folded into each player's run state, so a repeated
// call undoes its own delta before re-applying (idempotency contract). The
// `*After` values guard the undo: when the run was reset underneath us
// (game.ts restart restores base max_hp / boon_shield_regen_mult), the
// previous application is gone and there is nothing to undo.
interface AppliedTraits {
  maxHpDelta: number;
  shieldRegenFactor: number;
  maxHpAfter: number;
  shieldRegenAfter: number;
}

const appliedTraits = new WeakMap<Player, AppliedTraits>();

/**
 * Apply a loadout to the run (spec-loadout §2). Call AFTER world.reset() /
 * the restart body, before the run starts:
 * - slot 0 becomes the chosen weapon (level 1, targeting policy from the
 *   def); slot 1 (manual turret) and boon-granted weapons are untouched;
 * - the trait points are recorded on `player.loadoutTraits`;
 * - `player.weapon_slots` is re-rolled from the run seed (striker ≥ 3 adds
 *   +1, capped at 4 — see rollWeaponSlots);
 * - the max-HP delta is applied and hp is clamped to the new max;
 * - the shield-regen bonus folds multiplicatively into
 *   `player.boon_shield_regen_mult`.
 * Idempotent: re-applying the same (or a different) loadout to the same
 * run state undoes the previous application first.
 */
export function applyLoadout(world: WorldState, loadout: Loadout): void {
  const player = world.player;
  const normalized = normalizeLoadout(loadout);

  const def = WEAPON_DEFS.find((d) => d.id === normalized.weapon);
  if (!def) {
    // Unreachable after normalizeLoadout; guards against content regressions.
    throw new Error(`Loadout weapon ${normalized.weapon} not found in weapons.json`);
  }
  const slot0: WeaponState = {
    def_id: def.id,
    level: 1,
    evolved: false,
    cooldown_until: 0,
    last_target_id: 0,
    policy: def.targeting_policy,
    slot: 0
  };
  const slotIndex = player.weapons.findIndex((w) => w.slot === 0);
  if (slotIndex >= 0) {
    player.weapons[slotIndex] = slot0;
  } else {
    player.weapons.unshift(slot0);
  }

  player.loadoutTraits = {
    swift: normalized.swift,
    bulwark: normalized.bulwark,
    striker: normalized.striker
  };

  // Hull hardpoints: rolled from the run seed with the new striker spread.
  // Pure in (runSeed, striker), so idempotent re-application is safe; the
  // HARDPOINT boon's mid-run increments only ever happen after this.
  player.weapon_slots = rollWeaponSlots(world.runSeed, normalized.striker);

  const prev = appliedTraits.get(player);
  if (
    prev &&
    player.max_hp === prev.maxHpAfter &&
    player.boon_shield_regen_mult === prev.shieldRegenAfter
  ) {
    player.max_hp -= prev.maxHpDelta;
    player.boon_shield_regen_mult /= prev.shieldRegenFactor;
  }

  const mods = getTraitMods(player);
  const shieldRegenFactor = 1 + mods.shieldRegenMultBonus;
  player.max_hp += mods.maxHpDelta;
  player.boon_shield_regen_mult *= shieldRegenFactor;
  appliedTraits.set(player, {
    maxHpDelta: mods.maxHpDelta,
    shieldRegenFactor,
    maxHpAfter: player.max_hp,
    shieldRegenAfter: player.boon_shield_regen_mult
  });

  // Clamp hp to the new max (the trait HP cost never leaves hp above it).
  player.hp = Math.min(player.hp, player.max_hp);
}
